import { useEffect, useMemo, useState } from "react";
import {
  Clock,
  Edit,
  Eye,
  EyeOff,
  FileText,
  Headphones,
  Loader2,
  MoreVertical,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getCategoryLabel, normalizeCategoryList } from "@/lib/category-config";
import AudioUploadDialog from "@/components/admin/AudioUploadDialog";
import AudioEditDialog from "@/components/admin/AudioEditDialog";

interface AudioRow {
  id: string;
  title: string;
  description: string | null;
  audio_url: string;
  thumbnail_url: string | null;
  jlpt_level: "N5" | "N4" | "N3" | "N2" | "N1";
  category: string;
  categories?: string[];
  is_premium: boolean;
  is_published: boolean;
  duration: number;
  view_count: number;
  created_at: string;
  subtitle_count?: number;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

export default function AudioManagement() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [audios, setAudios] = useState<AudioRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editingAudio, setEditingAudio] = useState<AudioRow | null>(null);
  const [deletingAudio, setDeletingAudio] = useState<AudioRow | null>(null);

  const fetchAudios = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from("audios").select("*").order("created_at", { ascending: false });
      if (error) throw error;

      const withSubtitleCount = await Promise.all(
        (data || []).map(async (audio: any) => {
          const { count } = await supabase
            .from("audio_subtitles")
            .select("*", { count: "exact", head: true })
            .eq("audio_id", audio.id);

          return {
            ...(audio as AudioRow),
            subtitle_count: count || 0,
          };
        }),
      );

      setAudios(withSubtitleCount);
    } catch (error) {
      console.error("Failed to fetch audios", error);
      toast({ title: "加载音频列表失败", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudios();
  }, []);

  const handleTogglePublish = async (audio: AudioRow) => {
    try {
      const { error } = await supabase
        .from("audios")
        .update({ is_published: !audio.is_published })
        .eq("id", audio.id);
      if (error) throw error;

      setAudios((prev) =>
        prev.map((row) =>
          row.id === audio.id ? { ...row, is_published: !row.is_published } : row,
        ),
      );

      toast({ title: audio.is_published ? "已下架" : "已发布" });
    } catch (error) {
      console.error("Failed to toggle publish", error);
      toast({ title: "操作失败", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deletingAudio) return;

    try {
      await supabase.from("audio_subtitles").delete().eq("audio_id", deletingAudio.id);

      const { error } = await supabase.from("audios").delete().eq("id", deletingAudio.id);
      if (error) throw error;

      setAudios((prev) => prev.filter((row) => row.id !== deletingAudio.id));
      toast({ title: "音频已删除" });
    } catch (error) {
      console.error("Failed to delete audio", error);
      toast({ title: "删除失败", variant: "destructive" });
    } finally {
      setDeletingAudio(null);
    }
  };

  const filteredAudios = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase();
    if (!keyword) return audios;

    return audios.filter((audio) => {
      return (
        audio.title.toLowerCase().includes(keyword) ||
        (audio.description || "").toLowerCase().includes(keyword)
      );
    });
  }, [audios, searchQuery]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Headphones className="h-6 w-6" />
            音频管理
          </h1>
          <p className="text-muted-foreground">管理平台上的音频内容与字幕</p>
        </div>
        <Button onClick={() => setUploadOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          上传音频
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="搜索音频..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="pl-10"
          />
        </div>
        <p className="text-sm text-muted-foreground">共 {filteredAudios.length} 条</p>
      </div>

      <div className="rounded-lg border">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredAudios.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            {searchQuery ? "没有匹配的音频" : "暂无音频，点击上方按钮上传"}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>音频</TableHead>
                <TableHead>等级</TableHead>
                <TableHead>分类</TableHead>
                <TableHead>字幕</TableHead>
                <TableHead>时长</TableHead>
                <TableHead>状态</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAudios.map((audio) => {
                const categories = normalizeCategoryList([
                  ...(Array.isArray(audio.categories) ? audio.categories : []),
                  audio.category,
                ]);

                return (
                  <TableRow key={audio.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {audio.thumbnail_url ? (
                          <img
                            src={audio.thumbnail_url}
                            alt={audio.title}
                            className="h-10 w-16 rounded object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-16 items-center justify-center rounded bg-muted">
                            <Headphones className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="max-w-[220px] truncate font-medium">{audio.title}</p>
                          {audio.description ? (
                            <p className="max-w-[220px] truncate text-xs text-muted-foreground">{audio.description}</p>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <Badge variant="outline">{audio.jlpt_level}</Badge>
                    </TableCell>

                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {categories.map((category) => (
                          <Badge key={category} variant="outline" className="text-xs">
                            {getCategoryLabel(category)}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <FileText className="h-3.5 w-3.5" />
                        {audio.subtitle_count || 0}
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        {formatDuration(audio.duration || 0)}
                      </div>
                    </TableCell>

                    <TableCell>
                      <Badge variant={audio.is_published ? "default" : "secondary"}>
                        {audio.is_published ? "已发布" : "未发布"}
                      </Badge>
                    </TableCell>

                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditingAudio(audio)}>
                            <Edit className="mr-2 h-4 w-4" />
                            编辑
                          </DropdownMenuItem>

                          <DropdownMenuItem onClick={() => handleTogglePublish(audio)}>
                            {audio.is_published ? (
                              <>
                                <EyeOff className="mr-2 h-4 w-4" />
                                下架
                              </>
                            ) : (
                              <>
                                <Eye className="mr-2 h-4 w-4" />
                                发布
                              </>
                            )}
                          </DropdownMenuItem>

                          <DropdownMenuItem className="text-destructive" onClick={() => setDeletingAudio(audio)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            删除
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {user ? (
        <AudioUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} userId={user.id} onSuccess={fetchAudios} />
      ) : null}

      {editingAudio ? (
        <AudioEditDialog
          open={!!editingAudio}
          onOpenChange={(open) => {
            if (!open) setEditingAudio(null);
          }}
          audio={editingAudio}
          onSuccess={() => {
            fetchAudios();
            setEditingAudio(null);
          }}
        />
      ) : null}

      <AlertDialog open={!!deletingAudio} onOpenChange={(open) => !open && setDeletingAudio(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除音频《{deletingAudio?.title}》吗？该操作不可撤销，相关字幕也会被删除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
